import { gql, useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { Header, Modal, ModalThatSlides, Table } from '../../components';
import { Search } from '../../components/Header/components';
import { Block, Button, SelectInput, Text, TextInput } from '../../components/layout';
import ButtonAction from '../../components/Table/components/ButtonAction';
import { SvgIconEdit, SvgIconModalDelete, SvgIconTrash } from '../../svgs';
import validateFormSchema from '../../utils/validateFormSchema';

function Users() {
    const paginate = useRef(false);
    const [search, setSearch] = useState("");
    const [modalCreateIsOpen, setModalCreateIsOpen] = useState(false);
    const [modalDeleteIsOpen, setModalDeleteIsOpen] = useState(null);
    const [modalEditIsOpen, setModalEditIsOpen] = useState(null);

    const [result, setResult] = useState({ edges: [], pageInfo: { hasNextPage: false }, totalCount: 0 });

    const [queryUsers, { loading }] = useLazyQuery(
        gql`
            query users($first: Int, $after: String, $search: String) {
                users(first: $first, after: $after, search: $search) {
                    edges {
                        node {
                            id
                            name
                            email
                            role
                        }
                        cursor
                    }
                    pageInfo {
                        startCursor
                        endCursor
                        hasNextPage
                    }
                    totalCount
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.users) {
                    setResult({
                        edges: paginate.current ? [...result.edges, ...data.users.edges] : data.users.edges,
                        pageInfo: data.users.pageInfo,
                        totalCount: data.users.totalCount
                    });
                    paginate.current = false;
                }
            }
        }
    );

    const [mutationUserCreate, { loading: loadingUserCreate }] = useMutation(
        gql`
            mutation userCreate($name: String!, $email: String!, $password: String!, $role: String!) {
                userCreate(name: $name, email: $email, password: $password, role: $role) {
                    id
                    name
                    email
                    role
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.userCreate) {
                    setModalCreateIsOpen(false);
                    requestData();
                }
            }
        }
    );

    const [mutationUserUpdate, { loading: loadingUserUpdate }] = useMutation(
        gql`
            mutation userUpdate($id: ID!, $name: String, $email: String, $password: String, $role: String) {
                userUpdate(id: $id, name: $name, email: $email, password: $password, role: $role) {
                    id
                    name
                    email
                    role
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.userUpdate) {
                    const index = result.edges.findIndex((edge) => edge.cursor == data.userUpdate.id);
                    setModalEditIsOpen(null);
                    if (index !== -1) {
                        result.edges[index].node = data.userUpdate;
                        setResult(...result);
                    }
                }
            }
        }
    );

    const [mutationUserDelete, { loading: loadingUserDelete }] = useMutation(
        gql`
            mutation userDelete($id: ID!) {
                userDelete(id: $id) {
                    id
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.userDelete) {
                    const index = result.edges.findIndex((edge) => edge.cursor == data.userDelete.id);
                    if (index !== -1) {
                        result.edges.splice(index, 1);
                        setModalDeleteIsOpen(null);
                        setResult([...data]);
                    }
                }
            }
        }
    );

    const requestData = (params = { next: false }) => {
        const { first, after, next } = params;

        if (next) {
            paginate.current = true;
        }

        queryUsers({
            variables: {
                first: first || 20,
                after: after || undefined,
                search: search !== "" ? search : undefined
            }
        });
    }

    useEffect(() => {
        requestData();
    }, [search]);

    const roles = {
        "role.developer": "Developer",
        "role.administrator": "Administrator"
    }

    return (
        <>
            <Block flex height="100%">
                <Header
                    leftChildren={<Search onChange={(e) => setSearch(e.target.value)} />}
                    rightChildren={<Button style={{ minWidth: 309 }} text="Add user" onClick={() => setModalCreateIsOpen(true)} />}
                />
                <Block height="100%" mr={20} ml={20} style={{ overflow: "hidden" }}>
                    <Table
                        loading={loading}
                        columns={[
                            { label: "Name", key: "name" },
                            { label: "Email", key: "email" },
                            { label: "Role", key: "role" }
                        ]}
                        rows={result.edges.map((edge) => ({ ...edge.node, role: roles[edge.node.role], actions: <><ButtonAction icon={<SvgIconEdit />} text="Edit" onClick={() => setModalEditIsOpen(edge.node)} /><ButtonAction icon={<SvgIconTrash />} onClick={() => setModalDeleteIsOpen(edge.cursor)} /></> }))}
                        next={() => requestData({ next: true, after: result.pageInfo.endCursor })}
                        hasMore={result.pageInfo.hasNextPage}
                    />
                </Block>
            </Block>
            <Modal
                title="Create user"
                isOpen={modalCreateIsOpen}
                onRequestClose={() => setModalCreateIsOpen(false)}
            >
                <Text size={14} height={20}>Users</Text>
                <Block mt={28}>
                    <Formik
                        initialValues={{}}
                        validateOnChange={false}
                        validateOnBlur={false}
                        validate={values => {
                            return validateFormSchema(
                                yup.object().shape({
                                    name: yup.string().required(),
                                    email: yup.string().email().required(),
                                    password: yup.string().required(),
                                    role: yup.string().required(),
                                }),
                                values
                            )
                        }}
                        onSubmit={(values, { setSubmitting }) => {
                            setSubmitting(false);
                            mutationUserCreate({ variables: values });
                        }}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit}>
                                    <Block>
                                        <Block>
                                            <TextInput name="name" label="Name" type="text" onChange={handleChange} onBlur={handleBlur} value={values.name}  error={errors.name} />
                                        </Block>
                                        <Block mt={12}>
                                            <TextInput name="email" label="Email" type="text" onChange={handleChange} onBlur={handleBlur} value={values.email}  error={errors.email} />
                                        </Block>
                                        <Block mt={12}>
                                            <TextInput name="password" label="Password" type="password" onChange={handleChange} onBlur={handleBlur} value={values.password}  error={errors.password} />
                                        </Block>
                                        <Block mt={12}>
                                            <SelectInput label="Role" name="role" onBlur={handleBlur} options={Object.keys(roles).map((roleKey) => ({ value: roleKey, label: roles[roleKey] }))} value={values.role} error={errors.role} />
                                        </Block>
                                        <Block mt={44}>
                                            <Button type="submit" text="Submit" loading={loadingUserCreate} />
                                        </Block>
                                    </Block>
                                </form>
                            )
                        }}
                    </Formik>
                </Block>
            </Modal>
            <Modal
                width={280}
                title="Delete user"
                isOpen={!!modalDeleteIsOpen}
                onRequestClose={() => setModalDeleteIsOpen(null)}
            >
                <Block mb={20}>
                    <SvgIconModalDelete />
                </Block>
                <Text size={18} height={24}>Are you sure that you want to delete this user?</Text>
                <Text size={12} height={14} mt={8}>This action it’s irreversible, it will erase all the data from this user.</Text>
                <Block mt={32}>
                    <Button text="Delete" loading={loadingUserDelete} onClick={() => mutationUserDelete({ variables: { id: modalDeleteIsOpen } })} />
                </Block>
            </Modal>
            <ModalThatSlides title="Update user" width="820px" isOpen={!!modalEditIsOpen} onRequestClose={() => setModalEditIsOpen(null)}>
                <Block>
                    <Formik
                        enableReinitialize
                        initialValues={modalEditIsOpen || {}}
                        validateOnChange={false}
                        validateOnBlur={false}
                        validate={values => {
                            return validateFormSchema(
                                yup.object().shape({
                                    name: yup.string().required(),
                                    email: yup.string().email().required(),
                                    password: yup.string(),
                                    role: yup.string().required(),
                                }),
                                values
                            )
                        }}
                        onSubmit={(values, { setSubmitting }) => {
                            setSubmitting(false);
                            mutationUserUpdate({
                                variables: {
                                    id: modalEditIsOpen.id,
                                    name: values.name,
                                    email: values.email,
                                    password: values.password,
                                    role: values.role
                                }
                            })
                        }}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit} autoComplete="off">
                                    <Block flex color="white" pt={16} pb={16} pl={24} pr={24}>
                                        <Text size={14} height={20} mb={4}>Generic</Text>
                                        <Block mt={12}>
                                            <TextInput name="name" label="Name" type="text" value={values.name} onChange={handleChange} onBlur={handleBlur} value={values.name} error={errors.name} />
                                        </Block>
                                        <Block mt={12}>
                                            <TextInput name="email" label="Email" type="text" value={values.email} onChange={handleChange} onBlur={handleBlur} value={values.email} error={errors.email} />
                                        </Block>
                                        <Block mt={12}>
                                            <TextInput name="password" label="Password" type="password" onChange={handleChange} onBlur={handleBlur} value={values.password} error={errors.password} />
                                        </Block>
                                        <Block mt={12}>
                                            <SelectInput label="Role" name="role" onBlur={handleBlur} options={[{ value: "role.developer", label: "Developer" }, { value: "role.administrator", label: "Administrator" }]} value={values.role} error={errors.role} />
                                        </Block>
                                        <Block mt={28}>
                                            <Button type="submit" text="Save" loading={loadingUserUpdate} />
                                        </Block>
                                    </Block>
                                </form>
                            )
                        }}
                    </Formik>
                </Block>
            </ModalThatSlides>
        </>
    );
}

export default Users;